import { CONTACT } from "@/lib/constants";
import { AlertTriangle, Info, Scale, ShieldAlert } from "lucide-react";

const SECTIONS = [
    {
        icon: Info,
        title: "For informational and educational purposes only",
        body: "All content on All About Printers — including reviews, buying guides, comparisons, FAQs, and blog posts — is provided for general information and educational purposes only. Nothing on this site constitutes professional advice, endorsement, or a purchase recommendation tailored to your specific circumstances. Always consult the manufacturer, retailer, or a qualified technician for advice specific to your situation, workflow, or budget.",
    },
    {
        icon: ShieldAlert,
        title: "No endorsement or sponsorship",
        body: "All About Printers is an independent publication. Unless explicitly stated in a specific article or review with a clear \"Sponsored,\" \"Paid Partnership,\" or \"Ad\" label, our content is not endorsed by, sponsored by, or affiliated with any printer manufacturer, brand, retailer, or third party. Mentions of brand names (HP, Canon, Epson, Brother, Bambu Lab, Prusa, Creality, and others) are for identification and editorial reference only.",
    },
    {
        icon: Scale,
        title: "Trademarks and brand names",
        body: "All product names, logos, brand names, trademarks, and registered trademarks referenced on this site are the property of their respective owners. Their use here is nominative — meaning we use them only to refer to the product being discussed — and does not imply any endorsement, affiliation, partnership, or sponsorship by the trademark holder unless specifically stated.",
    },
    {
        icon: AlertTriangle,
        title: "Accuracy and no warranty",
        body: "We make reasonable efforts to keep specifications, pricing, and product information current, but printer specs change frequently and typos happen. Content is provided \"as is\" without warranty of any kind, express or implied. Prices, availability, features, and manufacturer support may differ from what is described here. Always verify important details with the manufacturer or authorized retailer before making a purchase decision.",
    },
];

export default function Disclaimer() {
    return (
        <div data-testid="disclaimer-page" className="container-tight py-16 md:py-24 max-w-3xl">
            <div className="eyebrow">Legal</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-white mt-3">Disclaimer</h1>
            <p className="text-zinc-500 mt-3 text-sm">Last updated: February 20, 2026</p>

            <div className="mt-6 p-5 border border-yellow-500/30 bg-yellow-500/5 rounded-sm" data-testid="disclaimer-key-banner">
                <p className="text-sm text-yellow-100 leading-relaxed">
                    <strong className="text-yellow-300">In short:</strong> This site is for educational and informational purposes only.
                    We are not endorsed by, sponsored by, or officially affiliated with any printer brand unless a specific piece of
                    content clearly states otherwise.
                </p>
            </div>

            <div className="mt-10 space-y-10 text-zinc-300 leading-relaxed">
                {SECTIONS.map((s, i) => (
                    <section key={s.title} data-testid={`disclaimer-section-${i + 1}`}>
                        <div className="flex items-center gap-3 mb-3">
                            <div className="h-9 w-9 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0">
                                <s.icon className="h-4 w-4 text-blue-400" />
                            </div>
                            <h2 className="font-display text-xl font-semibold text-white">{s.title}</h2>
                        </div>
                        <p>{s.body}</p>
                    </section>
                ))}

                <section>
                    <h2 className="font-display text-xl font-semibold text-white mb-3">Editorial process</h2>
                    <p>
                        Reviews and buying guides are written by our editorial team using publicly available specifications,
                        manufacturer documentation, independent research, user community feedback, and — where feasible — direct familiarity
                        with the products described. We do not operate a certified testing laboratory, and specific throughput, cost-per-page,
                        or reliability figures should be treated as informed editorial estimates rather than certified test results.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-semibold text-white mb-3">Affiliate and advertising disclosure</h2>
                    <p>
                        All About Printers may participate in affiliate programs and display advertising (including Google Ads).
                        When you click certain links and make a purchase, we may earn a small commission at no additional cost to you.
                        These commercial arrangements never influence our editorial opinion, and any sponsored or paid content is
                        clearly labeled as such at the top of the piece. See our{" "}
                        <a href="/privacy" className="text-blue-400 hover:text-blue-300">Privacy Policy</a> for details on how
                        third-party ad platforms may use cookies to measure the effectiveness of our advertising.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-semibold text-white mb-3">Limitation of liability</h2>
                    <p>
                        To the maximum extent permitted by applicable law, All About Printers and its contributors are not liable
                        for any direct, indirect, incidental, consequential, or special damages arising from your use of this site,
                        reliance on its content, or purchase decisions based on its recommendations.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-semibold text-white mb-3">External links</h2>
                    <p>
                        Our site may contain links to third-party websites that we do not operate or control. We are not responsible
                        for the content, privacy practices, or accuracy of any linked third-party sites. Following an outbound link
                        is at your own discretion.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-semibold text-white mb-3">Changes to this disclaimer</h2>
                    <p>
                        We may update this disclaimer from time to time. Material changes will be reflected in the &quot;last updated&quot; date
                        at the top of this page. Continued use of the site after a change constitutes acceptance of the revised terms.
                    </p>
                </section>

                <section>
                    <h2 className="font-display text-xl font-semibold text-white mb-3">Contact</h2>
                    <p>
                        Questions about this disclaimer? Email us at <a className="text-blue-400 hover:text-blue-300" href={CONTACT.emailHref}>{CONTACT.email}</a> or
                        call <a className="text-blue-400 hover:text-blue-300" href={CONTACT.phoneHref}>{CONTACT.phoneDisplay}</a>.
                    </p>
                </section>
            </div>
        </div>
    );
}
